import { FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function LoginPage() {
  const { user, authRequired, registrationOpen, login, register } = useAuth();
  const location = useLocation();
  const from =
    (location.state as { from?: string } | null)?.from && authRequired
      ? String((location.state as { from?: string }).from)
      : "/search";

  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "register") {
        await register(email, password, displayName);
      } else {
        await login(email, password);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Request failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4">
      <div className="w-full max-w-md border border-outline-variant bg-surface-container-low p-8">
        <h1 className="text-xl font-black text-primary tracking-tight">ORION-1</h1>
        <p className="text-[10px] uppercase tracking-widest text-on-surface-variant mt-1 mb-8">
          Strategy Desk sign in
        </p>

        <form className="space-y-4" onSubmit={onSubmit}>
          {mode === "register" && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-on-surface-variant">
                Display name
              </span>
              <input
                className="desk-input mt-1 w-full px-3 py-2 text-sm"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="name"
              />
            </label>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-on-surface-variant">
              Email
            </span>
            <input
              className="desk-input mt-1 w-full px-3 py-2 text-sm"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-on-surface-variant">
              Password
            </span>
            <input
              className="desk-input mt-1 w-full px-3 py-2 text-sm"
              type="password"
              required
              minLength={mode === "register" ? 10 : 1}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
            />
          </label>

          {error && (
            <p className="text-sm text-red-400" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-on-primary font-bold uppercase tracking-wider text-xs py-3 disabled:opacity-50"
          >
            {submitting ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
          </button>
        </form>

        {registrationOpen && (
          <p className="mt-6 text-sm text-on-surface-variant">
            {mode === "login" ? (
              <>
                Need an account?{" "}
                <button
                  type="button"
                  className="text-primary underline"
                  onClick={() => setMode("register")}
                >
                  Register
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  className="text-primary underline"
                  onClick={() => setMode("login")}
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
